<template>
  <div id="service-page">
    <SfBreadcrumbs
      class="sf-breadcrumbs--mobile mobile-only"
      :breadcrumbs="breadcrumbs"
    />
    <div class="service-page">
      <div class="togas-container">
        <div class="sf-content-pages">
          <section
            class="sf-content-pages__section"
            :class="{ 'is-active': isOnSubpage}"
          >
            <div class="sf-content-pages__sidebar">
              <h2
                v-t="'Customer Care'"
                class="sf-content-pages__title desktop-only"
              />
              <div
                v-for="linkGroup in sidebarLinkGroups"
                :key="linkGroup.id"
              >
                <SfList class="sf-content-pages__list">
                  <li
                    v-for="item in linkGroup.items"
                    :key="item.label"
                    v-show="!item.onlyForWebsites || item.onlyForWebsites.includes(websiteCode)"
                    class="sf-content-pages__list-item sf-list__item"
                  >
                    <SfMenuItem
                      :label="$t(item.label)"
                      :link="localePath(item.link)"
                      class="sf-content-pages__menu"
                    >
                      <template #account-icon>
                        <span class="sf-chevron sf-chevron--right mobile-only" />
                      </template>
                    </SfMenuItem>
                  </li>
                </SfList>
              </div>

              <div class="sf-content-pages__sidebar-contact desktop-only">
                <h2
                  v-t="'Contact us'"
                  class="sf-content-pages__title sf-content-pages__sidebar-contact-title"
                />
                <SfList class="sf-content-pages__list">
                  <li class="sf-content-pages__list-item sf-list__item"
                  >
                    <SfMenuItem
                      :label="$t('Reach out to our Client Services')"
                      class="sf-content-pages__menu contact-us__text"
                    />
                  </li>
                  <SfButton
                    class="contact-us__button sf-button--outlined sf-button--medium"
                    :link="localePath('/contact-us')"
                  >
                    {{ $t('CONTACT US')}}
                  </SfButton>
                </SfList>
              </div>
            </div>

            <div class="sf-content-pages__content">
              <router-view
                :class="websiteCode"
                :key="subpage.id"
                :tabs="currentTabs"
              />
            </div>
          </section>
        </div>
      </div>
    </div>

    <div class="service-page__bottom-banner" v-if="subpage[0] === 'loyalty-program'">
      <picture class="img-block">
        <source :srcset="require(`~/assets/images/customer-care/${subpage}-d.webp`)"
                media="screen and (min-width: 1024px)"
        />
        <img :src="require(`~/assets/images/customer-care/${subpage}-m.webp`)"
             :alt="$t('Customer Care')"
        />
      </picture>
    </div>

  </div>
</template>
<script>
import { SfButton, SfBreadcrumbs, SfTabs, SfMenuItem, SfList } from '~/components';
import { computed, ref, useRoute, useContext } from '@nuxtjs/composition-api';
import { useConfigStore } from "~/stores/config";
import { onSSR } from '@vue-storefront/core';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';

export default {
  name: 'CustomerCare',
  components: {
    SfButton,
    SfBreadcrumbs,
    SfTabs,
    SfMenuItem,
    SfList,
  },
  head() {
    return {
      title: this.$i18n.t('Customer Care')
    };
  },
  setup() {
    const { i18n, localePath } = useContext();
    const route = useRoute();
    const { storeConfig } = useConfigStore();
    const { addTags } = useCache();
    const websiteCode = computed(() => storeConfig.website_code);

    onSSR(() => {
      addTags([{ prefix: CacheTagPrefix.View, value: `customer-care/${subpage.value}` }]);
    })

    const sidebarLinkGroups = [
      {
        items: [
          {
            id: 'shipment-returns',
            label: 'Shipment and Returns',
            link: '/customer-care/shipment-returns',
            tabs: [
              {
                title: 'Orders & Shipment',
                cmsBlockId: 'customer_care:shipment-returns/orders-shipment',
              },
              {
                title: 'Returns & Exchanges',
                cmsBlockId: 'customer_care:shipment-returns/returns-exchanges',
              },
              {
                title: 'Order Cancellation',
                cmsBlockId: 'customer_care:shipment-returns/order-cancellation',
              },
            ]
          },
          {
            id: 'privacy-policy',
            label: 'Privacy Policy',
            link: '/customer-care/privacy-policy',
            tabs: [
              {
                title: 'Privacy Policy',
                cmsBlockId: 'customer_care:privacy-policy/privacy-policy',
              },
              {
                title: 'Privacy Policy',
                cmsBlockId: 'customer_care:privacy-policy/cookies-other-technologies',
              },
              {
                title: 'Privacy Policy',
                cmsBlockId: 'customer_care:privacy-policy/social-media',
              },
            ]
          },
          {
            id: 'impressum',
            label: 'Impressum',
            link: '/customer-care/impressum',
            tabs: [
              {
                title: 'Impressum',
                cmsBlockId: 'customer_care:impressum/impressum',
              },
            ],
            onlyForWebsites: ['togas_at_website']
          },
          {
            id: 'terms-conditions',
            label: 'Terms and Conditions',
            link: '/customer-care/terms-conditions',
            tabs: [
              {
                title: 'Terms and conditions',
                cmsBlockId: 'customer_care:terms-conditions/terms-conditions',
              },
              {
                title: 'Privacy and Security',
                cmsBlockId: 'customer_care:terms-conditions/privacy-security',
              },
            ]
          },
          {
            id: 'gift-packing',
            label: 'Gift Packaging',
            link: '/customer-care/gift-packing',
            tabs: [
              {
                title: 'Gift Packaging',
                cmsBlockId: 'customer_care:gift-packing/gift-packing',
              },
            ]
          },
          {
            id: 'loyalty-program',
            label: 'Loyalty Program',
            link: '/customer-care/loyalty-program',
            tabs: [
              {
                title: 'Loyalty Program',
                cmsBlockId: 'customer_care:gift-packing/loyalty-program',
              },
            ]
          },
          {
            id: 'accessibility-statement',
            label: 'Accessibility Statement',
            link: '/customer-care/accessibility-statement',
            tabs: [
              {
                title: 'Loyalty Program',
                cmsBlockId: 'customer_care:shipment-returns/accessibility-statement',
              },
            ],
            onlyForWebsites: ['togascom_us']

          },
        ],
      }
    ];

    const isOnSubpage = computed(() => route.value.matched.length > 1);
    const subpage = computed(() => route.value.path.split('/').splice(-1, 1));
    const pageName = computed(() => isOnSubpage.value
      ? i18n.t(sidebarLinkGroups[0].items.filter(item => item.id === subpage.value[0])[0].label)
      : i18n.t('Customer Care')
    );

    const currentTabs = computed(() => isOnSubpage.value
      ? sidebarLinkGroups[0].items.filter(item => item.id === subpage.value[0])[0].tabs
      : []
    );

    const breadcrumbs = ref([]);
    breadcrumbs.value = [
      {
        link: localePath(isOnSubpage.value ? '/customer-care' : '/'),
        text: '',
      },
      {
        link: '',
        text: pageName.value,
      }
    ];

    return {
      breadcrumbs,
      sidebarLinkGroups,
      isOnSubpage,
      subpage,
      currentTabs,
      websiteCode
    };
  },
};
</script>

<style lang="scss" scoped>
.service-page {

  @include for-desktop {
    margin-top: var(--sp_32);
  }
}

.nuxt-link-exact-active {
  font-weight: var(--font-weight--bold);
}

.sf-content-pages {
  --content-pages-height: auto;

  &__sidebar-contact {

    &-title {
      font-weight: var(--font-weight--bold);
      margin: var(--sp_40) 0 var(--sp_32);
    }

    &:before {
      content: '';
      display: block;
      width: calc(100% + 80px);
      height: 40px;
      background-color: var(--c-white-1);
      position: relative;
      left: -40px;
    }
  }
}

.togas-container {
  @include for-mobile {
    padding: 0;
  }
}

.contact-us__button {
  width: 100%;
}

.contact-us__text:hover {
  --menu-item-label-color: var(--c-black);
  cursor: default;
}

.togas_at_website {
  ::v-deep {

    .subtitle {
      font-weight: 600;
      font-style: italic;
      font-family: var(--font-family--inter);
    }

    .text {
      font-size: var(--font-size--sm);
      line-height: var(--line-height--base);
      font-family: var(--font-family--inter);
    }
  }
}
</style>
